// frontend/src/components/DataTable/chartSeriesUtils.js
import { DEVICE_TYPES, DIRECTION_MAP, ELECTROMAGNETIC_MAP } from '../../modules/deviceHandlers';
import { DOCUMENT_TYPE } from '../../modules/workflowConfig';

const DEFAULT_SERIES_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'
];

export const DUAL_TABLE_DEVICE_TYPES = new Set(['biconical_antenna', 'loop_antenna', 'receiving_antenna']);
const CAR_TABLE_FALLBACK_NAMES = ['PK', 'QP', 'AV'];
export const DETECTOR_NAME_MAP = { 峰值: 'PK', 准峰值: 'QP', 平均值: 'AV' };

const chartColorIndexCache = new Map();

const getScopedColor = (scope, key) => {
  if (!chartColorIndexCache.has(scope)) {
    chartColorIndexCache.set(scope, new Map());
  }

  const scopeMap = chartColorIndexCache.get(scope);

  if (!scopeMap.has(key)) {
    scopeMap.set(key, scopeMap.size);
  }

  const index = scopeMap.get(key);
  return getColorByIndex(index);
};

// 按索引生成稳定系列颜色。
const getColorByIndex = (index) => {
  if (index < DEFAULT_SERIES_COLORS.length) {
    return DEFAULT_SERIES_COLORS[index];
  }

  const goldenAngle = 137.508;
  const hue = (index * goldenAngle) % 360;
  const saturation = 60 + (index % 4) * 8;
  const lightness = 45 + (index % 3) * 6;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// 以作用域+系列名作为键返回稳定颜色。
export const getSeriesColorByName = (name, index, scope = 'default') => {
  return getColorByIndex(index);
};

// 生成频率坐标标签。
const formatFrequencyLabel = (value) => {
  if (value >= 1000) return `${(value / 1000).toFixed(1)} GHz`;
  if (value >= 1) return `${value.toFixed(2)} MHz`;
  if (value > 0) return `${value.toFixed(3)} MHz`;
  return `${value}`;
};

// 构建X/Y轴通用配置。
const getAxisConfig = (type, name, isX = false) => ({
  type,
  name,
  nameLocation: 'center',
  nameGap: isX ? 30 : 45,
  axisLine: { lineStyle: { color: '#666' } },
  splitLine: { lineStyle: { color: '#eee' } },
  ...(isX && {
    axisLabel: { formatter: formatFrequencyLabel },
    min: (value) => Math.max(0.01, value.min),
    logLabelBase: 10
  })
});

// 构建折线序列配置。
const getSeriesConfig = (data, color, name = '') => ({
  name,
  type: 'line',
  smooth: true,
  symbol: 'none',
  showSymbol: false,
  lineStyle: { width: 2, color },
  itemStyle: { color },
  data
});

// 将任意单元格值解析为数字。
const parseNumericValue = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const normalized = String(value).trim().replace(/[，]/g, ',').replace(/[−–—]/g, '-').replace(/,/g, '');
  const match = normalized.match(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/);
  return match ? Number(match[0]) : NaN;
};

// 从首行中优先匹配指定字段。
const getRowField = (table, preferredFields) => {
  const firstRow = table?.rows?.[0];
  if (!firstRow) return null;
  const keys = Object.keys(firstRow);

  for (const field of preferredFields) {
    const exact = keys.find((key) => key === field);
    if (exact) return exact;
  }

  for (const field of preferredFields) {
    const fuzzy = keys.find((key) => key.includes(field));
    if (fuzzy) return fuzzy;
  }

  return null;
};

// 提取图表坐标数据并按频率排序。
const extractData = (table, xField, yField) => (table.rows || [])
  .map((row) => {
    const xKey = Object.keys(row).find((key) => key.includes(xField)) || xField;
    const yKey = Object.keys(row).find((key) => key.includes(yField)) || yField;
    const x = parseNumericValue(row[xKey]);
    const y = parseNumericValue(row[yKey]);
    return Number.isFinite(x) && Number.isFinite(y) && x > 0 ? [x, y] : null;
  })
  .filter(Boolean)
  .sort((a, b) => a[0] - b[0]);

const getComparisonValueColumns = (table = {}, xField = '') => {
  const columns = Array.isArray(table?.columns) ? table.columns : [];
  return columns.filter((col) => {
    const normalized = String(col || '');
    if (!normalized || normalized === xField) return false;
    if (normalized === '序号' || normalized === '删除') return false;
    if (normalized.includes('差值')) return false;
    return true;
  });
};

// 获取序列最大频率用于X轴上限。
const getXAxisMax = (series) => {
  const max = series.reduce((currentMax, item) => Math.max(currentMax, ...item.data.map(([x]) => x)), 0);
  return max > 0 ? max : undefined;
};

// 生成双曲线设备系列名称。
const getDualSeriesName = (
  deviceType,
  table,
  index,
  showSourceFile = false
) => {
  const sourceFile = table?.metadata?.source_file;
  const sourcePrefix = showSourceFile && sourceFile ? `${sourceFile} - ` : '';
  const preferredTitle = table?.metadata?.display_name || table?.metadata?.matched_table_title || table?.metadata?.source_table_title;
  if (preferredTitle) {
    return `${sourcePrefix}${preferredTitle}`;
  }

  if (table.metadata?.direction || deviceType === 'biconical_antenna') {
    return `${sourcePrefix}${DIRECTION_MAP[table.metadata?.direction] || (index === 0 ? '水平' : '垂直')}`;
  }

  return `${sourcePrefix}${ELECTROMAGNETIC_MAP[table.metadata?.electromagnetic] || (index === 0 ? '磁场' : '电场')}`;
};

// 生成车规曲线系列名称。
const getCarSeriesName = (table, index) => {
  const category = table?.metadata?.category?.trim?.();
  const note = table?.metadata?.note?.trim?.();
  const normalizedNote = DETECTOR_NAME_MAP[note] || note || CAR_TABLE_FALLBACK_NAMES[index] || `Table${index + 1}`;
  return category ? `${category}-${normalizedNote}` : normalizedNote;
};

// 解析探头类设备的横纵轴字段映射。
const resolveProbeAxisFields = (table, chartConfig, deviceType) => {
  const currentProbeXField = getRowField(table, ['Frequency[MHz]', '频率[MHz]', 'Frequency', '频率', 'Freq']);
  const currentProbeYField = getRowField(table, ['Factor[dB]', 'Factor [dB]', '校准因子', '转移阻抗', 'Factor']);
  const isEFieldProbe = deviceType === 'e_field_probe' || (deviceType === 'probe' && table?.rows?.[0]?.['X[multip]'] !== undefined);
  const isCurrentProbeLike = deviceType === 'current_probe' || (deviceType === 'probe' && Boolean(currentProbeXField) && Boolean(currentProbeYField));
  return {
    currentProbeXField,
    currentProbeYField,
    isEFieldProbe,
    isCurrentProbeLike,
    xDataField: isEFieldProbe
      ? (getRowField(table, ['频率[MHz]', 'Frequency[MHz]', 'Frequency', '频率']) || chartConfig.x)
      : (isCurrentProbeLike ? currentProbeXField : chartConfig.x),
    yDataField: isCurrentProbeLike ? currentProbeYField : chartConfig.y,
  };
};

// 构建车规图表模型。
const buildCarChartModel = ({ tables, documentType, deviceType }) => {
  const carSeries = tables.flatMap((table, tableIndex) => {
    const seriesName = getCarSeriesName(table, tableIndex);
    return (table.rows || []).map((row) => {
      const startMHz = parseFloat(row['开始[MHz]']);
      const endMHz = parseFloat(row['终止[MHz]']);
      const startKey = Object.keys(row).find((key) => key.includes('开始') && key.includes('[dBμV]'));
      const endKey = Object.keys(row).find((key) => key.includes('终止') && key.includes('[dBμV]'));
      const startVal = parseFloat(row[startKey]);
      const endVal = parseFloat(row[endKey]);

      if (Number.isNaN(startMHz) || Number.isNaN(endMHz) || Number.isNaN(startVal) || Number.isNaN(endVal)) {
        return null;
      }

      const scope = `${documentType}-${deviceType}`;
      const color = getScopedColor(scope, seriesName);

      return {
        ...getSeriesConfig([[startMHz, startVal], [endMHz, endVal]], color, seriesName),
        lineStyle: {
          width: 2,
          color,
          shadowColor: 'rgba(30, 144, 255, 0.3)',
          shadowBlur: 10,
          shadowOffsetY: 5,
        }
      };
    }).filter(Boolean);
  });

  return {
    chartTitle: '频段限值曲线',
    gridLeft: '10%',
    xAxis: { ...getAxisConfig('log', '频率（MHz）', true), max: getXAxisMax(carSeries) },
    yAxis: getAxisConfig('value', '等级（dBμV/m）'),
    seriesData: carSeries,
  };
};

// 构建设备图表模型。
const buildDeviceChartModel = ({ tables, documentType, deviceType }) => {
  const sourceFiles = tables
    .map((t) => t?.metadata?.source_file)
    .filter((v) => typeof v === 'string' && v.trim());

  const uniqueSourceFiles = new Set(sourceFiles);

  const shouldShowSourceFile = uniqueSourceFiles.size > 1;
  const chartConfig = DEVICE_TYPES.find((item) => item.value === deviceType)?.chart || {
    x: '频率 (MHz)',
    y: '数值',
    title: '数据趋势图'
  };
  const probeInfo = resolveProbeAxisFields(tables[0], chartConfig, deviceType);
  const { currentProbeXField, currentProbeYField, isEFieldProbe, isCurrentProbeLike, xDataField, yDataField } = probeInfo;

  const comparisonValueColumns = getComparisonValueColumns(tables[0], xDataField);
  const isMergedComparisonTable = comparisonValueColumns.length >= 2 && tables.length === 1;

  let seriesData = [];
  if (isMergedComparisonTable) {
    seriesData = comparisonValueColumns
      .map((columnName, index) => getSeriesConfig(
        extractData(tables[0], xDataField, columnName),
        getSeriesColorByName(columnName, index, `${documentType}-${deviceType}-comparison`),
        columnName
      ))
      .filter((series) => series.data?.length > 0);
  } else if (DUAL_TABLE_DEVICE_TYPES.has(deviceType)) {
    const hasMultipleSourceFiles = new Set(
      tables.map((table) => table?.metadata?.source_file).filter((name) => typeof name === 'string' && name.trim())
    ).size > 1;
    const dualSeriesTables = hasMultipleSourceFiles ? tables : tables.slice(0, 2);
    seriesData = dualSeriesTables
      .map((table, index) => getSeriesConfig(
        extractData(table, chartConfig.x, chartConfig.y),
        getSeriesColorByName(getDualSeriesName(deviceType, table, index), index, `${documentType}-${deviceType}-dual`),
        getDualSeriesName(deviceType, table, index, shouldShowSourceFile)
      ))
      .filter((series) => series.data?.length > 0);
  } else if (isEFieldProbe) {
    const axes = [
      { field: 'X[multip]', name: 'X轴' },
      { field: 'Y[multip]', name: 'Y轴' },
      { field: 'Z[multip]', name: 'Z轴' }
    ];
    seriesData = axes
      .map((axis, index) => getSeriesConfig(
        extractData(tables[0], xDataField, axis.field),
        getSeriesColorByName(axis.name, index, `${documentType}-${deviceType}-axis`),
        axis.name
      ))
      .filter((series) => series.data?.length > 0);
  } else {
    seriesData = tables
      .map((table, index) => {
        const preferredTitle = table.metadata?.display_name || table.metadata?.matched_table_title || table.metadata?.source_table_title;
        const name = preferredTitle
          || (table.metadata?.direction
            ? DIRECTION_MAP[table.metadata.direction]
            : table.metadata?.electromagnetic
              ? ELECTROMAGNETIC_MAP[table.metadata.electromagnetic]
              : (table.metadata?.source_file || `曲线${index + 1}`));
        return getSeriesConfig(
          extractData(table, xDataField, yDataField),
          getSeriesColorByName(name, index, `${documentType}-${deviceType}-single`),
          name
        );
      })
      .filter((series) => series.data?.length > 0);
  }

  const xAxisField = isEFieldProbe ? xDataField : (isCurrentProbeLike ? currentProbeXField : chartConfig.x);
  const yAxisField = isEFieldProbe ? 'multip' : (isCurrentProbeLike ? currentProbeYField : chartConfig.y);

  return {
    chartTitle: chartConfig.title,
    gridLeft: '15%',
    xAxis: { ...getAxisConfig('log', xAxisField, true), max: getXAxisMax(seriesData) },
    yAxis: getAxisConfig('value', yAxisField),
    seriesData,
  };
};

// 构建图表视图模型供组件直接渲染。
export const buildChartModel = ({ tables, documentType, deviceType }) => {
  if (!tables?.length) {
    return { chartTitle: '数据可视化图表', gridLeft: '10%', xAxis: null, yAxis: null, seriesData: [] };
  }
  const isCarDocument = documentType === DOCUMENT_TYPE.STANDARD || documentType === DOCUMENT_TYPE.ENTERPRISE;
  return isCarDocument
    ? buildCarChartModel({ tables, documentType, deviceType })
    : buildDeviceChartModel({ tables, documentType, deviceType });
};

// 根据图例名称回查系列颜色。
export const getSeriesColor = (series, legendNames, name) => {
  const found = series.find((item) => item.name === name);
  if (found?.lineStyle?.color) return found.lineStyle.color;
  return getSeriesColorByName(name, legendNames.indexOf(name));
};

// 构建表格标题文本。
export const buildTableTitle = ({ deviceType, device_type: deviceTypeName, metadata, index, documentType }) => {
  const deviceDesc = DEVICE_TYPES.find((item) => item.value === deviceType)?.desc || '';
  const matchedTitle = metadata?.display_name?.trim?.() || metadata?.matched_table_title?.trim?.() || metadata?.title?.trim?.();
  if (matchedTitle) {
    return matchedTitle;
  }
  if (deviceTypeName) {
    let suffix = '';
    if (metadata.direction) {
      suffix = `（${DIRECTION_MAP[metadata.direction] || metadata.direction}）`;
    } else if (metadata.electromagnetic) {
      suffix = `（${ELECTROMAGNETIC_MAP[metadata.electromagnetic] || metadata.electromagnetic}）`;
    } else if (DUAL_TABLE_DEVICE_TYPES.has(deviceType) && (index === 0 || index === 1)) {
      const isDirection = metadata.direction || !metadata.electromagnetic;
      suffix = `（${isDirection ? (index === 0 ? '水平' : '垂直') : (index === 0 ? '磁场' : '电场')}）`;
    }
    const sourceFileSuffix = metadata?.source_file ? ` - ${metadata.source_file}` : '';
    return `${deviceTypeName}${deviceDesc ? ` - ${deviceDesc}` : ''}${suffix}${sourceFileSuffix}`;
  }

  const isCarDocument = documentType === DOCUMENT_TYPE.STANDARD || documentType === DOCUMENT_TYPE.ENTERPRISE;
  if (isCarDocument) {
    const category = metadata.category?.trim?.();
    const note = metadata.note?.trim?.();
    const normalizedNote = note || CAR_TABLE_FALLBACK_NAMES[index] || `数据表 ${index + 1}`;
    return category ? `${category}-${normalizedNote}` : normalizedNote;
  }

  return `数据表 ${index + 1}`;
};